import { useState, useEffect, useRef } from 'react';
import { IoCloseSharp } from 'react-icons/io5';
import { RxHamburgerMenu } from 'react-icons/rx';
import ChatbotSidebar from './ChatbotSidebar';
import ChatMessages from './ChatMessages';
import { useChatbot } from '@/services/chatbot.service';
import { useRootSelector } from '@/hooks/selector.hook';
import { Pagination, PaginationMessage } from '@/constants/pagination';
import TypingBlack from '@/assets/images/Typing-Black.gif';
import TypingWhite from '@/assets/images/Typing-White.gif';

interface Message {
  id: string; // Changed to string for better uniqueness
  type: 'bot' | 'user';
  content: string;
  timestamp: string; // Display time like "10:30 AM"
  sentAt: string; // ISO date string for date calculations
}

interface ChatbotUIProps {
  chatbotIcon: string;
  iconTitle: string;
  onClose: () => void;
  userName?: string; // Add userName prop for dynamic greeting
}

const ChatbotUI = ({ chatbotIcon, onClose, iconTitle, userName = 'User' }: ChatbotUIProps) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentSize, setCurrentSize] = useState(PaginationMessage.Size);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false); // Bot typing indicator
  const [isSendingMessage, setIsSendingMessage] = useState(false); // Flag to prevent message overwrite
  const [isClosing, setIsClosing] = useState(false); // Track closing animation for mobile
  const hasUserCreatedNewChat = useRef(false); // Track if user explicitly created new chat
  const lastMessageSentTime = useRef<number>(0); // Track last time a message was sent locally
  const { createSessionId, getAllHistoryChatbot, getAllMessageHistoryChatbot, sendMessage } =
    useChatbot();
  const { dataHistortChatbot, dataMessageChatbot, paginationMessage } = useRootSelector(
    (state) => state.chatbot,
  );

  // Show greeting when: 1) No messages (new chat) OR 2) All messages loaded (reached oldest message)
  const showGreeting =
    messages.length === 0 ||
    !!(paginationMessage?.totalElements && currentSize >= paginationMessage.totalElements);

  // Load chat history on mount
  useEffect(() => {
    const loadInitialChat = async () => {
      // First, get all chat history
      await getAllHistoryChatbot({
        Page: Pagination.Page,
        Size: Pagination.Size,
      });
    };

    loadInitialChat();
  }, [getAllHistoryChatbot]);

  // Load first chat when history is available (but not when user is actively using a session)
  useEffect(() => {
    // Check for sessionId in localStorage
    const sessionIdFromStorage = localStorage.getItem('sessionId');

    // Case 1: sessionId exists in localStorage but not in state -> Load that specific chat
    if (sessionIdFromStorage && !currentSessionId) {
      // Set state
      setCurrentSessionId(sessionIdFromStorage);
      setCurrentSize(PaginationMessage.Size);

      // Reset debounce timer to allow messages to load immediately
      lastMessageSentTime.current = 0;

      // Load messages for that chat
      getAllMessageHistoryChatbot(
        {
          Page: PaginationMessage.Page,
          Size: PaginationMessage.Size,
        },
        sessionIdFromStorage,
      );
      return;
    }

    // Case 2: No sessionId in localStorage and no current session -> Load first chat
    if (
      !sessionIdFromStorage &&
      !currentSessionId &&
      !hasUserCreatedNewChat.current &&
      dataHistortChatbot &&
      dataHistortChatbot.length > 0
    ) {
      const firstChat = dataHistortChatbot[0];

      // Store sessionId in localStorage and state
      localStorage.setItem('sessionId', firstChat.id);
      setCurrentSessionId(firstChat.id);
      setCurrentSize(PaginationMessage.Size);

      // Reset debounce timer to allow messages to load immediately
      lastMessageSentTime.current = 0;

      // Load messages for the first chat
      getAllMessageHistoryChatbot(
        {
          Page: PaginationMessage.Page,
          Size: PaginationMessage.Size,
        },
        firstChat.id,
      );
      return;
    }
  }, [dataHistortChatbot, currentSessionId, messages.length, getAllMessageHistoryChatbot]);

  // Update messages when dataMessageChatbot changes (but not when sending new message)
  useEffect(() => {
    const timeSinceLastSent = Date.now() - lastMessageSentTime.current;
    const shouldWaitForLocalMessage = timeSinceLastSent < 2000; // Wait 2 seconds after sending

    // Don't update if:
    // 1. Currently sending a message
    // 2. Just sent a message within last 2 seconds (to let local message display first)
    if (
      dataMessageChatbot &&
      dataMessageChatbot.length > 0 &&
      !isSendingMessage &&
      !shouldWaitForLocalMessage
    ) {
      // Sort messages by sentAt (oldest first)
      const sortedMessages = [...dataMessageChatbot].sort((a, b) => {
        return new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime();
      });

      const formattedMessages: Message[] = sortedMessages.map((msg, index) => ({
        // Use messageId-sentAt as unique string ID to prevent duplicates
        id: msg.messageId
          ? `${msg.messageId}-${msg.sentAt}`
          : `${new Date(msg.sentAt).getTime()}-${index}`,
        type: msg.role === 1 ? 'bot' : 'user', // role 1 = bot, role 0 = user
        content: msg.content,
        timestamp: new Date(msg.sentAt).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        }),
        sentAt: msg.sentAt, // Keep ISO date string
      }));

      setMessages(formattedMessages);
    }
  }, [dataMessageChatbot, isSendingMessage]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleNewChat = () => {
    // Set flag to prevent auto-loading first chat
    hasUserCreatedNewChat.current = true;

    // Clear messages for new chat
    setMessages([]);
    // Clear sessionId from localStorage for new chat
    localStorage.removeItem('sessionId');
    setCurrentSessionId(null);
    setCurrentSize(PaginationMessage.Size);
    setIsSidebarOpen(false); // Close sidebar when creating new chat
  };

  const handleSelectChat = (chatId: string) => {
    // Reset the new chat flag when user selects a chat
    hasUserCreatedNewChat.current = false;

    // Update sessionId in localStorage and state
    localStorage.setItem('sessionId', chatId);
    setCurrentSessionId(chatId);
    setCurrentSize(PaginationMessage.Size);

    // Reset debounce timer to allow messages to load immediately
    lastMessageSentTime.current = 0;

    // Load messages for the selected chat
    getAllMessageHistoryChatbot(
      {
        Page: PaginationMessage.Page,
        Size: PaginationMessage.Size,
      },
      chatId,
    );
  };

  const handleLoadMore = () => {
    if (currentSessionId) {
      const nextSize = currentSize + 2;
      setCurrentSize(nextSize);

      // Load more messages with increased size (Page stays at 1)
      getAllMessageHistoryChatbot(
        {
          Page: PaginationMessage.Page, // Always page 1
          Size: nextSize,
        },
        currentSessionId,
      );
    }
  };

  const handleSendMessage = async (message: string) => {
    if (!message.trim()) return;

    // Set flag to prevent useEffect from overwriting messages
    setIsSendingMessage(true);

    // Bước 1 & 3: Check and create session if needed
    // Priority: currentSessionId state > localStorage
    let sessionIdToUse = currentSessionId || localStorage.getItem('sessionId');

    // Track if this is a new chat (to refresh history later)
    const isNewChat = !sessionIdToUse;

    if (!sessionIdToUse) {
      const sessionData = await createSessionId();

      if (!sessionData || !sessionData.sessionId) {
        // Session creation failed - show error and abort
        alert('Failed to create chat session. Please try again.');
        setIsSendingMessage(false);
        // Keep hasUserCreatedNewChat flag true to prevent auto-loading another chat
        return;
      }

      // Save sessionId immediately to localStorage and state
      localStorage.setItem('sessionId', sessionData.sessionId);
      setCurrentSessionId(sessionData.sessionId);
      sessionIdToUse = sessionData.sessionId;

      // Reset the new chat flag since we now have an active session
      hasUserCreatedNewChat.current = false;
    }

    // Bước 2: Add user message to chat UI immediately
    const now = new Date();
    const timestamp = now.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      type: 'user',
      content: message,
      timestamp: timestamp,
      sentAt: now.toISOString(), // Add ISO date string
    };

    setMessages((prev) => [...prev, userMessage]);

    // Mark that we just sent a message locally
    lastMessageSentTime.current = Date.now();

    // Bước 4: Show typing indicator
    setIsTyping(true);

    // Send message to API and get bot response
    if (sessionIdToUse) {
      const response = await sendMessage(sessionIdToUse, message);

      if (response) {
        // Add bot response to messages
        const botMessage: Message = {
          id: `bot-${Date.now()}`,
          type: 'bot',
          content:
            response.response ||
            response.content ||
            response.message ||
            'Thank you for your message.',
          timestamp: new Date().toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          }),
          sentAt: new Date().toISOString(), // Add ISO date string
        };
        setMessages((prev) => {
          return [...prev, botMessage];
        });

        // If this was a new chat, refresh history to show it in sidebar
        if (isNewChat) {
          getAllHistoryChatbot({
            Page: Pagination.Page,
            Size: Pagination.Size,
          });
        }
      }
    }

    setIsTyping(false); // Hide typing indicator
    setIsSendingMessage(false); // Reset flag
  };

  // Handle close with animation on mobile
  const handleClose = () => {
    setIsClosing(true);
    // Wait for animation to complete before calling onClose
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 300); // Match animation duration
  };

  return (
    <div
      className={`
      fixed inset-0 md:relative md:inset-auto
      w-full h-full md:w-[45rem] xl:w-[54rem] md:h-[49rem] xl:h-[70rem]
      overflow-hidden md:rounded-[2.5rem] bg-[#28BA8C] dark:bg-[#4BD2A7] 
      px-[1rem] md:px-[0.3rem] relative z-50
      transition-transform duration-300 ease-out
      ${
        isClosing
          ? 'translate-y-full md:translate-y-0'
          : 'translate-y-0 animate-slideUp md:animate-none'
      }
    `}
    >
      <div className="flex items-center justify-between px-4 pt-8 pb-3 md:py-3">
        <div className="flex items-center gap-3">
          <img src={chatbotIcon} alt="Chatbot" className="w-[6.3rem] h-[6.4rem]" />
          <div className="flex flex-col gap-[0.5rem]">
            <div className="flex gap-[0.6rem]">
              <img src={iconTitle} alt="NezenX" />
              <p className="text-[#DDFCF2] dark:text-[#000000] text-[1.8rem]">Pilot</p>
            </div>
            <div className="flex items-center gap-1 text-[1rem] text-[#EEFFF9] dark:text-[#0C0E12]">
              <span className="w-[0.7rem] h-[0.7rem] rounded-full bg-[#00F1A9] dark:bg-[#00AA77]"></span>
              Active
            </div>
          </div>
        </div>
        <button
          onClick={handleClose}
          className="text-[#FFFFFF] dark:text-[#0C0E12] hover:opacity-70 transition-opacity z-50"
          aria-label="Close chatbot"
        >
          <IoCloseSharp className="size-10" />
        </button>
      </div>

      <div className="md:mt-[0rem] xl:mt-[1.9rem] h-[86%] md:h-[83%] xl:h-[85%] bg-[#F5F5F5] dark:bg-[#151B19] rounded-[2.3rem] relative overflow-hidden">
        {/* Sidebar Component */}
        <ChatbotSidebar
          isOpen={isSidebarOpen}
          onClose={toggleSidebar}
          onNewChat={handleNewChat}
          onSelectChat={handleSelectChat}
          currentSessionId={currentSessionId}
        />

        {/* Main content area */}
        <div className="h-full flex flex-col">
          {/* Hamburger Menu */}
          <div className="pt-[2.6rem] pl-[2.7rem] pb-4">
            <RxHamburgerMenu
              className="size-[2.5rem] cursor-pointer text-[#1F1F1F] dark:text-[#FFFFFF]"
              onClick={toggleSidebar}
            />
          </div>

          {/* Chat Messages */}
          <div className="flex-1 overflow-hidden">
            <ChatMessages
              userName={userName}
              messages={messages}
              onSendMessage={handleSendMessage}
              onLoadMore={handleLoadMore}
              showGreeting={showGreeting}
              isTyping={isTyping}
              typingBlack={TypingBlack}
              typingWhite={TypingWhite}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatbotUI;
